/*
 * business-card/preflight.js — the checks a print shop runs, before you pay.
 *
 * WHY THIS IS THE MOST IMPORTANT FILE IN THE TOOL
 * ──────────────────────────────────────────────
 * Everything else here makes a card. This decides whether the card SURVIVES.
 * The failure mode this category actually has is not "the design is ugly" — it
 * is a file that comes back rejected two days later with "нет вылетов" or
 * "низкое разрешение", or worse, one that is not rejected and prints wrong.
 *
 * Every online card maker surveyed on 2026-08-23 ships the file and lets the
 * shop's preflight find the problems. That is a strange division of labour: the
 * tool knows the geometry exactly, in millimetres, at the moment the mistake is
 * made, and stays silent; the shop finds out later, in a queue, and charges for
 * the round trip. Moving the check here costs nothing and removes the entire
 * delay.
 *
 * THE RULES ARE PHYSICAL, NOT STYLISTIC
 * ─────────────────────────────────────
 * Nothing here is taste. Each check corresponds to a mechanism:
 *
 *   safe zone      — the guillotine drifts ±1 mm on a stack; content near the
 *                    trim gets shaved.
 *   missing bleed  — background stopping at the trim leaves bare paper when the
 *                    cut lands outside it. The white-sliver complaint.
 *   raster dpi     — the press images at 300 dpi; fewer pixels than that is
 *                    literally less detail than the paper can hold.
 *   hairlines      — a stroke below ~0.25 pt is at the limit of the plate.
 *   rich black     — CMY under K needs four plates to register; on small type
 *                    they cannot, and the letter gets a coloured ghost.
 *   ink coverage   — over ~300 % the sheet does not dry and sets off onto the
 *                    next card in the stack.
 *   QR module      — a symbol whose module is under ~0.4 mm is not readable by
 *                    a phone at arm's length, however correct the encoding is.
 *
 * SEVERITY MEANS SOMETHING
 * ────────────────────────
 *   'error' — a shop would reject this, or it will visibly misprint.
 *   'warn'  — it will print, and you probably will not like it.
 *   'tip'   — it will print fine; there is a better choice available.
 *
 * A tool that grades everything as a scary red warning teaches people to
 * ignore it, so the ladder is kept honest: most cards should come back clean.
 *
 * Pure module: no DOM, no browser API.
 */
(function (root, factory) {
    'use strict';
    var api = factory();
    if (typeof module !== 'undefined' && module.exports) module.exports = api;
    if (root) root.CardPreflight = api;
})(typeof self !== 'undefined' ? self : (typeof window !== 'undefined' ? window : null), function () {
    'use strict';

    var MM_PER_PT = 25.4 / 72;

    function mmToPt(mm) { return mm / MM_PER_PT; }

    /* Bounding box of a drawable, in trim-space millimetres. Text is measured
     * from its BASELINE, so the box has to be reconstructed: up by roughly the
     * ascent, down by the descent, across by the widest line. Getting this
     * wrong in the safe direction (too big) is fine; too small means the check
     * passes something that gets cut. */
    /* Contrast of one text block against the pixels actually under it.
     * `photos` are the BAKED canvases, keyed the same way the renderer keys
     * them; without one (a redraw that has not baked yet) the answer is null
     * and the check stays silent rather than inventing a verdict. */
    function photoContrast(textIt, photoIt, photos, measure, ink) {
        var CP = (typeof window !== 'undefined') && window.CardPhoto;
        var cv = photos && photoIt.pkey && photos[photoIt.pkey];
        if (!CP || !cv || !cv.width) return null;
        var tb = boundsOf(textIt, measure);
        var pxPerMm = cv.width / photoIt.w;
        var L = CP.luminanceIn(cv, {
            x: (tb.x - photoIt.x) * pxPerMm, y: (tb.y - photoIt.y) * pxPerMm,
            w: Math.max(1, tb.w * pxPerMm), h: Math.max(1, tb.h * pxPerMm),
        });
        if (!L || !ink.luminance) return null;
        var lt = ink.luminance(ink.cmykToRgb(textIt.color.c, textIt.color.m, textIt.color.y, textIt.color.k));
        /* The worst patch under the block, not its average: light type fails
         * over the brightest pixel, dark type over the darkest. */
        var lb = lt > 0.5 ? L.max : L.min;
        var hi = Math.max(lt, lb), lo = Math.min(lt, lb);
        return Math.round(((hi + 0.05) / (lo + 0.05)) * 100) / 100;
    }

    function boundsOf(it, measure) {
        switch (it.type) {
            case 'photo':
            case 'rect': case 'frame': case 'image': case 'qr': {
                var s = it.size || 0;
                return {
                    x: it.x, y: it.y,
                    w: it.w != null ? it.w : s,
                    h: it.h != null ? it.h : s,
                };
            }
            case 'line':
                return { x: it.x, y: it.y - it.stroke / 2, w: it.w, h: (it.h || 0) + it.stroke };
            case 'icon':
                return { x: it.x, y: it.y, w: it.size, h: it.size };
            case 'poly': {
                var xs = it.points.map(function (p) { return p[0]; });
                var ys = it.points.map(function (p) { return p[1]; });
                return {
                    x: Math.min.apply(null, xs), y: Math.min.apply(null, ys),
                    w: Math.max.apply(null, xs) - Math.min.apply(null, xs),
                    h: Math.max.apply(null, ys) - Math.min.apply(null, ys),
                };
            }
            case 'text': {
                var widest = 0;
                for (var i = 0; i < it.lines.length; i++) {
                    var w = measure ? measure(it.lines[i], it) : it.lines[i].length * it.size * 0.5;
                    if (w > widest) widest = w;
                }
                var top = it.y - it.size * 0.78;
                var bottom = it.y + (it.lines.length - 1) * it.lineHeight + it.size * 0.24;
                var x = it.x;
                if (it.align === 'center') x = it.x + (it.w - widest) / 2;
                else if (it.align === 'right') x = it.x + it.w - widest;
                return { x: x, y: top, w: widest, h: bottom - top };
            }
        }
        return { x: 0, y: 0, w: 0, h: 0 };
    }

    /* Does this element deliberately live in the bleed? A background does; a
     * phone number does not. Told apart by whether the geometry starts outside
     * the trim, which is what `expandBleed` produced. */
    function isBleedElement(b) {
        return b.x < -0.001 || b.y < -0.001;
    }

    function run(ctx) {
        var out = [];
        var boxes = ctx.boxes;
        var lim = ctx.limits;
        var ink = ctx.ink;
        var measure = ctx.measure;
        var sides = ctx.sides;                 // [{ side, items, warnings }]
        var doc = ctx.doc;

        var trimW = boxes.w, trimH = boxes.h, safe = boxes.safe, bleed = boxes.bleed;

        sides.forEach(function (S) {
            var items = S.items;
            var side = S.side;

            /* ── 1. content outside the safe zone ─────────────────────────── */
            items.forEach(function (it) {
                var b = boundsOf(it, measure);
                if (isBleedElement(b)) return;           // backgrounds are meant to
                if (it.type === 'rect' || it.type === 'frame' || it.type === 'poly') return;
                var over = Math.max(
                    safe - b.x,
                    safe - b.y,
                    (b.x + b.w) - (trimW - safe),
                    (b.y + b.h) - (trimH - safe)
                );
                if (over <= 0.05) return;
                var pastTrim = b.x < -0.05 || b.y < -0.05 ||
                    (b.x + b.w) > trimW + 0.05 || (b.y + b.h) > trimH + 0.05;
                out.push({
                    code: pastTrim ? 'outside_trim' : 'outside_safe',
                    severity: pastTrim ? 'error' : 'warn',
                    side: side, type: it.type, field: it.field || null, id: it.id || null,
                    mm: Math.round(over * 10) / 10,
                    sample: it.type === 'text' ? it.lines.join(' ') : null,
                });
            });

            /* ── 2. a background that stops at the trim ────────────────────
             * The white-sliver bug, and the reason bleed exists at all. Caught
             * geometrically: a filled area whose edge lands ON the trim line
             * (within the cut tolerance) but does not continue past it. */
            items.forEach(function (it) {
                if (it.type !== 'rect' && it.type !== 'poly') return;
                var b = boundsOf(it, measure);
                var touches = [];
                if (Math.abs(b.x) < 0.6 && b.x > -0.05) touches.push('left');
                if (Math.abs(b.y) < 0.6 && b.y > -0.05) touches.push('top');
                if (Math.abs((b.x + b.w) - trimW) < 0.6 && (b.x + b.w) < trimW + 0.05) touches.push('right');
                if (Math.abs((b.y + b.h) - trimH) < 0.6 && (b.y + b.h) < trimH + 0.05) touches.push('bottom');
                if (touches.length) {
                    out.push({
                        code: 'no_bleed', severity: 'error', side: side,
                        edges: touches, need: bleed,
                    });
                }
            });

            /* ── 3. type size ─────────────────────────────────────────────── */
            items.forEach(function (it) {
                if (it.type !== 'text') return;
                var pt = mmToPt(it.size);
                if (pt < lim.minTextPt - 0.05) {
                    out.push({
                        code: 'text_too_small', severity: 'error', side: side, id: it.id || null,
                        pt: Math.round(pt * 10) / 10, field: it.field,
                        sample: it.lines.join(' '), min: lim.minTextPt,
                    });
                } else if (pt < lim.comfortTextPt - 0.05) {
                    out.push({
                        code: 'text_small', severity: 'warn', side: side, id: it.id || null,
                        pt: Math.round(pt * 10) / 10, field: it.field,
                        sample: it.lines.join(' '), min: lim.comfortTextPt,
                    });
                }
            });

            /* ── 4. rich black on small type ──────────────────────────────── */
            items.forEach(function (it) {
                if (it.type !== 'text') return;
                if (!ink.isRichBlack(it.color)) return;
                if (mmToPt(it.size) >= lim.richBlackMinPt) return;
                out.push({
                    code: 'rich_black_text', severity: 'warn', side: side, id: it.id || null,
                    pt: Math.round(mmToPt(it.size) * 10) / 10, field: it.field,
                    sample: it.lines.join(' '),
                });
            });

            /* ── 5. hairlines ─────────────────────────────────────────────── */
            items.forEach(function (it) {
                var s = it.stroke;
                if (s == null || s <= 0) return;
                if (s >= lim.minStrokeMm - 1e-6) return;
                out.push({
                    code: 'hairline', severity: 'warn', side: side,
                    mm: Math.round(s * 1000) / 1000, min: lim.minStrokeMm,
                });
            });

            /* ── 6. ink coverage ──────────────────────────────────────────── */
            items.forEach(function (it) {
                if (!it.color) return;
                var t = ink.tac(it.color);
                if (t <= lim.maxInkPct) return;
                out.push({
                    code: 'ink_over', severity: 'error', side: side,
                    tac: t, max: lim.maxInkPct,
                });
            });

            /* ── 7. raster resolution ─────────────────────────────────────── */
            items.forEach(function (it) {
                if (it.type !== 'image' || it.vector) return;
                if (!it.dpi) return;
                if (it.dpi >= lim.goodDpi - 5) return;
                out.push({
                    code: it.dpi < lim.minDpi ? 'logo_low_dpi' : 'logo_soft_dpi',
                    severity: it.dpi < lim.minDpi ? 'error' : 'warn',
                    side: side, dpi: it.dpi, want: lim.goodDpi, id: it.id || null,
                });
            });

            /* ── 7b. a full-colour logo dropped on a dark panel ──────────────
             * A bitmap cannot be recoloured, so a dark mark on the dark back of
             * a card is simply invisible — and it is invisible in the preview
             * too, which is why people ship it. One-colour mode solves it
             * exactly (the stencil takes the paper ink), so this is a tip with
             * an obvious action rather than a warning about nothing. */
            items.forEach(function (it) {
                if (it.type !== 'image' || !it.onColor || it.mono) return;
                out.push({ code: 'logo_on_dark', severity: 'tip', side: side, id: it.id || null });
            });

            /* ── 8. the QR, if there is one ───────────────────────────────── */
            items.forEach(function (it) {
                if (it.type !== 'qr' || !it.plan) return;
                if (it.plan.level === 'unscannable' || it.plan.level === 'too_big') {
                    out.push({
                        code: 'qr_unscannable', severity: 'error', side: side, id: it.id || null,
                        moduleMm: it.plan.moduleMm, needMm: it.plan.neededMm, size: it.size,
                    });
                } else if (it.plan.level === 'tight') {
                    out.push({
                        code: 'qr_tight', severity: 'warn', side: side, id: it.id || null,
                        moduleMm: it.plan.moduleMm, needMm: it.plan.neededMm, size: it.size,
                    });
                }
                /* A QR needs its quiet zone INSIDE the card. The symbol itself
                 * already carries four modules of it, so what this catches is a
                 * QR pushed so close to the trim that the cut eats the border —
                 * a symbol that looks complete on screen and will not acquire. */
                var b = boundsOf(it, measure);
                if (b.x < safe - 0.05 || b.y < safe - 0.05 ||
                    b.x + b.w > trimW - safe + 0.05 || b.y + b.h > trimH - safe + 0.05) {
                    out.push({ code: 'qr_near_edge', severity: 'warn', side: side, id: it.id || null });
                }
            });

            /* ── 8b. the background photograph ────────────────────────────
             * Two questions a photo card fails on, both invisible on screen.
             *
             * RESOLUTION is graded at the size the picture is PLACED at, not at
             * the size of the file. A phone photograph is 4000 px and prints
             * beautifully at 90 mm — until it is zoomed to 3×, at which point a
             * third of it is being asked to cover 90 mm and it is 380 dpi of
             * nothing.
             *
             * The thresholds are the logo's: below `minDpi` the print shop
             * rejects it, below `goodDpi` it prints soft. Reusing them is
             * deliberate — one number for "how sharp must a raster be on this
             * card" is one number to argue about. */
            items.forEach(function (it) {
                if (it.type !== 'photo' || !it.dpi) return;
                if (it.dpi >= lim.goodDpi - 5) return;
                out.push({
                    code: it.dpi < lim.minDpi ? 'bg_low_dpi' : 'bg_soft_dpi',
                    severity: it.dpi < lim.minDpi ? 'error' : 'warn',
                    side: side, id: it.id || 'bg',
                    dpi: it.dpi, want: lim.goodDpi,
                });
            });

            /* ── 9. contrast, on the printed colours ────────────────────────
             * ⚠️ A PHOTOGRAPH HAS NO COLOUR — it has a range of them, and the
             * text has to survive the worst part of that range, not the mean.
             * Grading against the average is how "текст поверх картинки" ships
             * with a name that is perfectly readable except across the one
             * bright window in the top left. So where a photo is what lies
             * under a block, the check reads the BAKED pixels — after the blur,
             * the veil and the tint, because those are the pixels that print —
             * and grades the block on its worst patch. */
            items.forEach(function (it) {
                if (it.type !== 'text') return;
                var under = backgroundUnder(it, items, measure, ctx.palette.paper);
                var ratio;
                if (under && under.__photo) {
                    ratio = photoContrast(it, under.__photo, ctx.photos, measure, ink);
                    if (ratio == null) return;
                    if (ratio < 2.2) {
                        out.push({
                            code: 'bg_text_unreadable', severity: 'error', side: side, id: it.id || null,
                            ratio: ratio, field: it.field, sample: it.lines.join(' '),
                        });
                    } else if (ratio < 3.4) {
                        out.push({
                            code: 'bg_text_thin', severity: 'warn', side: side, id: it.id || null,
                            ratio: ratio, field: it.field, sample: it.lines.join(' '),
                        });
                    }
                    return;
                }
                var bg = under;
                ratio = ink.contrast(it.color, bg);
                /* 3:1 is the WCAG floor for large text and a fair proxy for
                 * "readable ink on paper". Below 2 it is decorative. */
                if (ratio < 2.2) {
                    out.push({
                        code: 'low_contrast', severity: 'error', side: side, id: it.id || null,
                        ratio: ratio, field: it.field, sample: it.lines.join(' '),
                    });
                } else if (ratio < 3.4) {
                    out.push({
                        code: 'contrast_thin', severity: 'warn', side: side, id: it.id || null,
                        ratio: ratio, field: it.field, sample: it.lines.join(' '),
                    });
                }
            });

            /* ── 10. anything layout could not fit ────────────────────────── */
            (S.warnings || []).forEach(function (w) {
                if (w.code === 'overflow') {
                    out.push({ code: 'overflow', severity: 'error', side: side,
                        field: w.field, id: w.id || null, sample: w.text });
                }
            });
        });

        /* ── 10b. is the contact information even VALID? ──────────────────────
         * The most expensive mistake in this category is not a layout fault: it
         * is a phone number with a missing digit on a thousand printed cards.
         * Nothing on the card looks wrong, the shop prints exactly what it was
         * sent, and the error is discovered when nobody calls.
         *
         * These are deliberately conservative — a WARNING for shapes that are
         * almost certainly a typo, never an error, because address formats vary
         * more than any regular expression believes. The comma-for-dot case is
         * first because it is the one people actually make: `.ru` and `,ru`
         * share a key.
         */
        /* ── the logo's own colours move on press ────────────────────────────
         * The preview now draws the logo the way it will print, which is the
         * honest thing to do and also alarming without a word: the person
         * uploaded #005bff and the card shows #0d77c6. Measured 2026-08-23,
         * nine of eighteen real Russian brand colours move perceptibly, so this
         * is the common case rather than the exotic one.
         *
         * ⚠️ Raised ONCE, here, rather than per side — the shift is a property
         * of the FILE, and the same logo is usually on both sides, so the
         * per-side version printed the identical sentence twice. Not a
         * cosmetic detail: a panel that repeats itself teaches the reader to
         * stop reading it. */
        (function () {
            var worst = 0, where = null;
            sides.forEach(function (S) {
                (S.items || []).forEach(function (it) {
                    if (it.type !== 'image' || it.mono || !it.shift) return;
                    if (it.shift > worst) { worst = it.shift; where = S.side; }
                });
            });
            if (worst >= 12) {
                out.push({ code: 'logo_gamut', severity: 'warn', side: where, id: 'logo', shift: worst });
            }
        })();

        var d0 = doc.data || {};
        if (d0.email && !/^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/.test(d0.email.trim())) {
            out.push({ code: 'bad_email', severity: 'warn', sample: d0.email, id: 't:email', side: 'front' });
        }
        ['phone', 'phone2'].forEach(function (k) {
            if (!d0[k]) return;
            var digits = String(d0[k]).replace(/\D+/g, '');
            if (digits.length < 7 || digits.length > 15) {
                out.push({ code: 'bad_phone', severity: 'warn', sample: d0[k],
                    n: digits.length, id: 't:' + k, side: 'front' });
            }
        });
        if (d0.site) {
            var site = String(d0.site).trim();
            if (/,/.test(site) || /\s/.test(site) || !/\.[a-zA-Zа-яА-Я]{2,}/.test(site)) {
                out.push({ code: 'bad_site', severity: 'warn', sample: site, id: 't:site', side: 'front' });
            }
        }

        /* ── 10b. our sample is still printed on their card ───────────────────
         * The page opens on a FINISHED card — real name, real phone, real
         * address — because an empty canvas is where three quarters of visitors
         * leave. The cost of that decision is this failure: somebody replaces
         * the name, likes what they see, and orders a thousand cards carrying
         * OUR telephone number. Nothing looks broken; it is discovered by
         * nobody ever calling.
         *
         * ⚠️ The first fix for this was to WIPE every other field the moment a
         * first keystroke landed anywhere. It is worse than the disease, and it
         * was reported as a bug within a day of shipping: a user who starts by
         * correcting one word on the card watches the company, the phone, the
         * mail, the site and the address vanish at once, with no message and no
         * visible way back. Destroying the user's view to protect them from a
         * mistake they have not made yet is not a guard, it is a second bug.
         *
         * So the sample is TRACKED, not destroyed, and reported here like every
         * other defect this tool catches: named, pointed at, one click to fix.
         * `touched` keeps it quiet on the opening screen, where every field is
         * still ours and saying so would be noise.
         */
        var samp = ctx.sample;
        if (samp && samp.touched && samp.fields && samp.fields.length) {
            out.push({
                code: 'sample_left', severity: 'error',
                fields: samp.fields.slice(),
                n: samp.fields.length,
                /* Point the on-card flag at the first one so the issue has
                 * somewhere to land; the body names all of them. */
                id: 't:' + samp.fields[0], side: 'front',
            });
        }

        /* ── 11. is it usable as a business card at all? ──────────────────────
         * Not a print rule — a purpose rule, and the one no competitor checks.
         * A card with no way to reach the person is a rectangle of paper. It is
         * a 'tip' rather than an error because a QR-only or company-only card
         * is a legitimate choice; the point is that it should be a choice.
         */
        var d = doc.data || {};
        if (!d.phone && !d.email && !d.site && !(doc.qr && doc.qr.enabled)) {
            out.push({ code: 'no_contact', severity: 'warn' });
        }
        if (!d.name && !d.company) {
            out.push({ code: 'no_identity', severity: 'warn' });
        }

        /* ── 12. bleed set to zero ────────────────────────────────────────── */
        if (bleed < 0.5 && anyBleedArt(sides)) {
            out.push({ code: 'bleed_zero', severity: 'error' });
        }

        return {
            issues: out,
            errors: out.filter(function (i) { return i.severity === 'error'; }).length,
            warnings: out.filter(function (i) { return i.severity === 'warn'; }).length,
            tips: out.filter(function (i) { return i.severity === 'tip'; }).length,
            clean: out.length === 0,
        };
    }

    function anyBleedArt(sides) {
        for (var i = 0; i < sides.length; i++) {
            var items = sides[i].items;
            for (var j = 0; j < items.length; j++) {
                var it = items[j];
                if ((it.type === 'rect' || it.type === 'poly') && it.x <= 0.01 && it.y <= 0.01) return true;
            }
        }
        return false;
    }

    /* What is behind this text? The last filled area drawn before it that most
     * of the text sits on. Painter's order matters: an 'ink' panel drawn after
     * a 'paper' background is what the text actually sits on, and taking the
     * first match instead would grade against the wrong one.
     *
     * ⚠️ MAJORITY OVERLAP, NOT CONTAINMENT. Requiring the text box to be fully
     * inside the panel — the obvious version — fails the moment a descender
     * pokes one tenth of a millimetre past the panel's edge, and the check then
     * falls back to paper and reports white-on-white. That is not a rare edge
     * case: it fired on every band-top card in the preset matrix, where the
     * company name deliberately sits on the band's baseline. 60 % is the point
     * past which the text is unambiguously on the panel rather than straddling
     * an edge.
     */
    function backgroundUnder(textIt, items, measure, fallback) {
        var tb = boundsOf(textIt, measure);
        var area = Math.max(1e-6, tb.w * tb.h);
        var found = fallback;
        for (var i = 0; i < items.length; i++) {
            var it = items[i];
            if (it === textIt) break;
            var isPhoto = it.type === 'photo';
            if (!isPhoto && it.type !== 'rect' && it.type !== 'poly') continue;
            var b = boundsOf(it, measure);
            var ox = Math.max(0, Math.min(tb.x + tb.w, b.x + b.w) - Math.max(tb.x, b.x));
            var oy = Math.max(0, Math.min(tb.y + tb.h, b.y + b.h) - Math.max(tb.y, b.y));
            /* Last one wins, photo or plate alike: a colour panel painted ON a
             * photograph is what the text actually sits on, and so is a
             * photograph laid over a preset's colour plate. Ordering is the
             * whole answer here — asking "is there a photo anywhere" would
             * grade a name inside an opaque white box against the sky behind
             * it. */
            if ((ox * oy) / area >= 0.6) found = isPhoto ? { __photo: it } : it.color;
        }
        return found;
    }

    return {
        run: run,
        boundsOf: boundsOf,
        backgroundUnder: backgroundUnder,
        mmToPt: mmToPt,
    };
});
